import usePageTitle from "../utils/usePageTitle";
import { useEffect, useState } from "react";
import api from "../utils/api";

const ROLES = ["admin", "creator", "viewer"];
const ROLE_BADGES = { admin: "status-quote", creator: "status-active", viewer: "status-discontinued" };

const UsersPage = () => {
  usePageTitle("Users");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [error, setError] = useState("");
  const [tempPassword, setTempPassword] = useState(null);

  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("viewer");
  const [newAuthProvider, setNewAuthProvider] = useState("local");
  const [newFullName, setNewFullName] = useState("");

  const [editRole, setEditRole] = useState("");
  const [editFullName, setEditFullName] = useState("");

  const fetchUsers = async () => {
    try { const res = await api.get("/api/users"); setUsers(res.data); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const getInitials = (email, fullName) => {
    if (fullName) {
      const parts = fullName.split(" ");
      return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : fullName[0].toUpperCase();
    }
    const parts = email?.split("@")[0].split(".") || ["?"];
    return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : (parts[0][0] || "?").toUpperCase();
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setError("");
    try {
      await api.post("/api/users", { email: newEmail, password: newAuthProvider === "local" ? newPassword : undefined, role: newRole, authProvider: newAuthProvider, fullName: newFullName });
      setNewEmail(""); setNewPassword(""); setNewRole("viewer"); setNewAuthProvider("local"); setNewFullName("");
      setShowCreate(false); fetchUsers();
    } catch (err) { setError(err.response?.data?.error || "Failed"); }
  };

  const handleUpdate = async () => {
    setError(""); setTempPassword(null);
    try {
      const body = {};
      if (editRole && editRole !== editingUser.role) body.role = editRole;
      if (editFullName !== (editingUser.fullName || "")) body.fullName = editFullName;
      if (Object.keys(body).length === 0) { setEditingUser(null); return; }
      await api.put(`/api/users/${editingUser._id}`, body);
      setEditingUser(null); fetchUsers();
    } catch (err) { setError(err.response?.data?.error || "Failed"); }
  };

  const handleResetPassword = async () => {
    try {
      const res = await api.put(`/api/users/${editingUser._id}`, { resetPassword: true });
      setTempPassword(res.data.tempPassword);
    } catch (err) { setError(err.response?.data?.error || "Failed"); }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user ${user.email}?`)) return;
    try { await api.delete(`/api/users/${user._id}`); setEditingUser(null); fetchUsers(); }
    catch (err) { setError(err.response?.data?.error || "Failed"); }
  };

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-white" /></div>;

  return (
    <div>
      <div className="tl-card">
        <div className="tl-page-header">
          <h2>User Management</h2>
          <button className="btn btn-tl-primary" onClick={() => setShowCreate(true)}>+ New User</button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}<button className="btn-close float-end" onClick={() => setError("")} /></div>}

      <div className="tl-card tl-card-compact">
        <table className="tl-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Auth</th>
              <th>Role</th>
              <th>Last Login</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u._id} onClick={() => { setEditingUser(u); setEditRole(u.role); setEditFullName(u.fullName || ""); setTempPassword(null); setError(""); }}>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <div className="tl-avatar" style={{ width: 32, height: 32, fontSize: 12 }}>
                      {u.photoUrl ? <img src={u.photoUrl} alt="" /> : getInitials(u.email, u.fullName)}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600 }}>{u.fullName || u.email.split("@")[0]}</div>
                    </div>
                  </div>
                </td>
                <td style={{ color: "var(--tl-text-muted)" }}>{u.email}</td>
                <td><span className={`status-badge ${u.authProvider === "entra" ? "status-picked_up" : "status-active"}`}>{u.authProvider === "entra" ? "SSO" : "Local"}</span></td>
                <td><span className={`status-badge ${ROLE_BADGES[u.role]}`}>{u.role}</span></td>
                <td style={{ fontSize: "13px", color: "var(--tl-text-muted)" }}>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : "Never"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create */}
      {showCreate && (
        <div className="modal d-block tl-modal" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header"><h5 className="modal-title">New User</h5><button className="btn-close" onClick={() => setShowCreate(false)} /></div>
              <form onSubmit={handleCreate}>
                <div className="modal-body">
                  <div className="mb-3"><label className="tl-form-label">Email *</label><input className="form-control tl-form-control" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required /></div>
                  <div className="row mb-3">
                    <div className="col-md-6"><label className="tl-form-label">Full Name</label><input className="form-control tl-form-control" value={newFullName} onChange={(e) => setNewFullName(e.target.value)} /></div>
                    <div className="col-md-6"><label className="tl-form-label">Authentication</label><select className="form-select tl-form-control" value={newAuthProvider} onChange={(e) => setNewAuthProvider(e.target.value)}><option value="local">Local</option><option value="entra">SSO (Microsoft)</option></select></div>
                  </div>
                  {newAuthProvider === "local" && <div className="mb-3"><label className="tl-form-label">Password *</label><input type="password" className="form-control tl-form-control" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8} /></div>}
                  <div className="mb-3"><label className="tl-form-label">Role</label><select className="form-select tl-form-control" value={newRole} onChange={(e) => setNewRole(e.target.value)}>{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select></div>
                </div>
                <div className="modal-footer"><button className="btn btn-tl-secondary" type="button" onClick={() => setShowCreate(false)}>Cancel</button><button className="btn btn-tl-primary" type="submit">Save Changes</button></div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit */}
      {editingUser && (
        <div className="modal d-block tl-modal" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header"><h5 className="modal-title">Edit User: {editingUser.email.split("@")[0]}</h5><button className="btn-close" onClick={() => setEditingUser(null)} /></div>
              <div className="modal-body">
                <div className="tl-section-title" style={{ marginTop: 0 }}>User Details</div>
                <div className="mb-3"><label className="tl-form-label">Email</label><input className="form-control tl-form-control" value={editingUser.email} disabled style={{ backgroundColor: "#f8fafc" }} /></div>
                <div className="row mb-3">
                  <div className="col-md-6"><label className="tl-form-label">Full Name</label><input className="form-control tl-form-control" value={editFullName} onChange={(e) => setEditFullName(e.target.value)} /></div>
                  <div className="col-md-6"><label className="tl-form-label">Authentication</label><input className="form-control tl-form-control" value={editingUser.authProvider === "entra" ? "SSO (Microsoft Entra)" : "Local"} disabled style={{ backgroundColor: "#f8fafc" }} /></div>
                </div>

                <div className="tl-section-title">Security</div>
                <div className="mb-3"><label className="tl-form-label">Role</label><select className="form-select tl-form-control" value={editRole} onChange={(e) => setEditRole(e.target.value)}>{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select></div>

                {editingUser.authProvider === "local" && (
                  <>
                    <div className="tl-section-title">Reset Password</div>
                    <p className="text-muted small">Generate a temporary password. The user will be required to change it on next login.</p>
                    {tempPassword && (
                      <div className="alert alert-info py-2">
                        <strong>Temporary password:</strong> <code style={{ fontSize: "16px" }}>{tempPassword}</code>
                        <br /><small className="text-muted">Share this with the user securely.</small>
                      </div>
                    )}
                    <button className="btn btn-tl-primary" onClick={handleResetPassword}>Reset Password</button>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-danger btn-sm me-auto" style={{ borderRadius: "8px" }} onClick={() => handleDelete(editingUser)}>Delete User</button>
                <button className="btn btn-tl-secondary" onClick={() => setEditingUser(null)}>Cancel</button>
                <button className="btn btn-tl-primary" onClick={handleUpdate}>Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
